import '@/assets/appsflyer-smartscript';

import * as Sentry from '@sentry/react-router';
// eslint-disable-next-line no-restricted-imports
import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import { ErrorInfo, startTransition, StrictMode } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import { HydratedRouter } from 'react-router/dom';

import { initReactRouterClientSentry } from '@/monitoring/reactRouterSentry';

import i18n from './i18n';

initReactRouterClientSentry();

function synthesizeError(error: any, errorInfo: ErrorInfo) {
  const context: Record<string, any> = {};

  if (errorInfo?.componentStack) {
    // Generating this synthetic error allows monitoring services to apply sourcemaps
    // to unminify the stacktrace and make it readable.
    const errorBoundaryError = new Error(error?.message);
    errorBoundaryError.name = `React ErrorBoundary ${errorBoundaryError.name}`;
    errorBoundaryError.stack = errorInfo.componentStack;

    // REF: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error/cause
    error.cause = errorBoundaryError;

    // You can also just add the plain text as added event context.
    context.componentStack = errorInfo.componentStack;
  }

  return context;
}

function onError(error: any, errorInfo: ErrorInfo, callbackTag: string) {
  const context = synthesizeError(error, errorInfo);

  Sentry.captureException(error, {
    level: 'debug',
    tags: {
      'debug.type': 'hydration',
      'debug.callback': callbackTag,
      'debounce.key': `hydration__${callbackTag}`,
    },
    extra: {
      error,
      context,
      errorInfo,
    },
  });
}

async function hydrate() {
  await i18next
    .use(initReactI18next)
    .use(LanguageDetector)
    .use(Backend)
    .init({
      ...i18n,
      backend: { loadPath: '/i18n/{{ns}}_{{lng}}.json' },
      detection: {
        order: ['htmlTag'],
        caches: [],
      },
    });

  startTransition(() => {
    hydrateRoot(
      document,
      <StrictMode>
        <I18nextProvider i18n={i18next}>
          <HydratedRouter />
        </I18nextProvider>
      </StrictMode>,
      {
        onRecoverableError: (error, errorInfo) =>
          onError(error, errorInfo, 'onRecoverableError'),
        onCaughtError: (error, errorInfo) =>
          onError(error, errorInfo, 'onCaughtError'),
        onUncaughtError: (error, errorInfo) =>
          onError(error, errorInfo, 'onUncaughtError'),
      },
    );
  });
}

hydrate();
